
<script setup lang="ts">
import { useRoute } from "vue-router";
import Results from "../components/Results/Results.vue";
import { throttle } from "@/utils";
import { onMounted, ref, watch } from "vue";
import { useSessionTimeoutStore } from "../stores/sessionTimeout";
import TimeoutWarning from "../components/TimeoutWarning.vue";
import router from "../router";
import { ToastRenderer } from "../services/toast";
import LoadingQuotations from "../components/LoadingQuotations.vue";
import { useQuoteStore, QuoteState } from "../stores/quotes";
import AmsHandoffModal from "./AmsHandoffModal.vue";
import Modal from "../components/Modal.vue";

const route = useRoute();
const quoteStore = useQuoteStore();
const resetSessionTimerEvents = ["click", "focusin", "keydown", "scroll"];
const sessionTimeoutStore = useSessionTimeoutStore();

const resetTimeoutHandler = throttle(2000, () => {
    if (!sessionTimeoutStore.showTimeoutWarning()) {
        sessionTimeoutStore.touchSession();
    }
});

const showAmsHandoffModal = ref(false);

watch(
    () => quoteStore.quotations.some((premium) => premium.quoteGeneratedBy === "AMS"),
    (isAmsHandoffQuote) => {
        if (isAmsHandoffQuote && sessionStorage.getItem("amsHandoffModalConfirmed") !== "true") {
            showAmsHandoffModal.value = true;
        }
    },
);

onMounted(async () => {
    resetSessionTimerEvents.forEach(event => {
        document.addEventListener(event, resetTimeoutHandler);
    });

    sessionTimeoutStore.touchSession();

    setInterval(() => {
        sessionTimeoutStore.currentTime = Number(new Date());
    }, 100);

    quoteStore.pollQuotes(route.query.quotenumber?.toString());
});

function closeOverlay() {
    sessionTimeoutStore.activeOverlay = false;
}

watch(() => sessionTimeoutStore.activeOverlay, (activeOverlay) => {
    if (activeOverlay) {
        document.body.classList.add("stop-body-scroll");
    } else {
        document.body.classList.remove("stop-body-scroll");
    }
});

watch(() => sessionTimeoutStore.currentTime, () => {
    if (sessionTimeoutStore.currentTime > sessionTimeoutStore.endTime) {
        sessionTimeoutStore.resetSession();
        router.push({ name: "session_timeout" });
    }
});

watch(() => route.query.quotenumber, () => {
    quoteStore.pollQuotes(route.query.quotenumber?.toString());
});

function closeAmsHandoffModal() {
    showAmsHandoffModal.value = false;
    sessionStorage.setItem("amsHandoffModalConfirmed", "true");
}

</script>
<template>
    <div>
        <div
            :class="{
                'overlay': sessionTimeoutStore.showTimeoutWarning()
            }"
            @click="closeOverlay()"
        />
        <TimeoutWarning
            v-if="sessionTimeoutStore.shouldShowTimeoutWarning()"
            :time-left="sessionTimeoutStore.getTimeLeft()"
        />
        <div class="row">
            <div class="col-md-12">
                <LoadingQuotations v-if="quoteStore.state === QuoteState.Loading" />
                <Results v-if="quoteStore.state === QuoteState.Ready" />
            </div>
        </div>
    </div>
    <ToastRenderer />
    <Modal
        id="ams-handoff-modal"
        open-focus="close-ams-handoff-modal"
        :show="showAmsHandoffModal"
        :wide="true"
    >
        <template #body>
            <AmsHandoffModal
                @close="closeAmsHandoffModal"
            />
        </template>
    </Modal>
</template>
